/**
 * Custom Auth Context
 * Provides manual control over Auth0 authentication flow
 * Shows custom login page instead of automatic redirects
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { createAuth0Client } from '@auth0/auth0-spa-js';
import auth0Config from '../config/auth';
import { setTokenProvider, setLogoutHandler } from './axiosConfig';

const AuthContext = createContext({
  isLoading: true,
  isAuthenticated: false,
  user: null,
  tenant: null,
  error: null,
  isBootstrapping: false,
  loginWithRedirect: () => {},
  logout: () => {},
  getAccessTokenSilently: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [auth0Client, setAuth0Client] = useState(null);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const isInitialized = useRef(false);
  const [tenant, setTenant] = useState(null);

  const performAuthCheck = async (client) => {
    try {
      const authenticated = await client.isAuthenticated();
      setIsAuthenticated(authenticated);

      if (authenticated) {
        const userData = await client.getUser();

        // Set up axios token provider
        setTokenProvider(async () => {
          try {
            const token = await client.getTokenSilently();
            return token;
          } catch (error) {
            console.error('Error getting access token for axios:', error);
            return null;
          }
        });

        // Set up axios logout handler for 401 responses
        setLogoutHandler(async () => {
          setIsAuthenticated(false);
          setUser(null);
          setTenant(null);
          setTokenProvider(() => null);
          setLogoutHandler(null);
          
          try {
            await client.logout({
              logoutParams: {
                returnTo: window.location.origin,
              },
            });
          } catch (logoutError) {
            console.error('Error during automatic logout:', logoutError);
          }
        });

        // Call bootstrap endpoint
        try {
          const token = await client.getTokenSilently();
          const response = await fetch(`${auth0Config.apiBaseUrl}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const bootstrapData = await response.json();
            const enhancedUser = {
              ...userData,
              ...bootstrapData.user,
              tenant: bootstrapData.tenant
            };
            setUser(enhancedUser);
            setTenant(bootstrapData.tenant);

            

            localStorage.removeItem('auth_error');
          } else {
            setUser(userData);
          }
        } catch (bootstrapError) {
          console.error('Error calling bootstrap endpoint:', bootstrapError);
          setUser(userData);
        }
      } else {
        setTokenProvider(() => null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setIsAuthenticated(false);
      setUser(null);
      setTenant(null);
      setTokenProvider(() => null);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize Auth0 client
  useEffect(() => {
    const initAuth0 = async () => {
      if (isInitialized.current) {
        return;
      }

      isInitialized.current = true;

      try {
        const client = await createAuth0Client({
          domain: auth0Config.domain,
          clientId: auth0Config.clientId,
          authorizationParams: {
            redirect_uri: auth0Config.redirectUri,
            audience: auth0Config.audience,
            scope: auth0Config.scope,
          },
          cacheLocation: auth0Config.cacheLocation,
          useRefreshTokens: auth0Config.useRefreshTokens,
        });

        setAuth0Client(client);


        // Check if we're returning from Auth0 redirect
        const query = window.location.search;
        if (query.includes('code=') && query.includes('state=')) {
          try {
            await client.handleRedirectCallback();

            // Clean up the URL
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname
            );
          } catch (callbackError) {
            console.error('Auth0 callback error:', callbackError);

            // Handle specific "Invalid state" error
            if (
              callbackError.error === 'invalid_state' ||
              callbackError.message?.includes('Invalid state')
            ) {
              console.log(
                'Invalid state detected, clearing URL and continuing...'
              );
              // Clear the URL and continue - user might have refreshed the callback page
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname
              );
              // Don't throw the error, just log it and continue
            } else {
              throw callbackError;
            }
          }
        }

        await performAuthCheck(client);
      } catch (err) {
        console.error('Auth0 initialization error:', err);
        setError(err.message);
        setIsAuthenticated(false);
        setIsLoading(false); // Move this INTO the catch block
      }
    };

    initAuth0();
  }, []);

  // Login function
  const loginWithRedirect = async (options = {}) => {
    if (!auth0Client) {
      throw new Error('Auth0 client not initialized');
    }

    try {
      await auth0Client.loginWithRedirect({
        authorizationParams: {
          ...options,
        },
      });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
      throw err;
    }
  };

  // Logout function
  const logout = async (options = {}) => {
    if (!auth0Client) return;

    try {
      // Clear local state first
      setIsAuthenticated(false);
      setUser(null);
      setTenant(null);
      setTokenProvider(() => null);
      setLogoutHandler(null);

      await auth0Client.logout({
        logoutParams: {
          returnTo: window.location.origin,
          ...options,
        },
      });
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message);
    }
  };

  // Get access token
  const getAccessTokenSilently = async (options = {}) => {
    if (!auth0Client) {
      throw new Error('Auth0 client not initialized');
    }

    try {
      return await auth0Client.getTokenSilently(options);
    } catch (err) {
      console.error('Token error:', err);
      throw err;
    }
  };

  const value = {
    isLoading,
    isAuthenticated,
    user,
    tenant,
    error,
    isBootstrapping,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
